import { Component, effect, inject, input, OnDestroy, OnInit, output, DestroyRef, ChangeDetectionStrategy, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatSelectChange, MatSelect, MatSelectTrigger } from '@angular/material/select';
import { LookupRefService } from '@miq/platform';
import { L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { ReplaySubject } from 'rxjs';
import { DefaultSelectionValue, FormGroupValue, InputPropOption, InputProps, InputPropsEnum } from '@miq/shared-models';
import { NgTemplateOutlet, AsyncPipe, UpperCasePipe } from '@angular/common';
import { FilterBarLabelComponent } from '../filter-bar-label/filter-bar-label.component';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-simple-select',
  templateUrl: './simple-select.component.html',
  styleUrl: './simple-select.component.scss',
  imports: [
    FilterBarLabelComponent,
    MatFormField,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    MatOption,
    MatSelectTrigger,
    MatIcon,
    AsyncPipe,
    UpperCasePipe,
    L10nTranslatePipe
],
})
export class SimpleSelectComponent<T = DefaultSelectionValue> implements OnInit, OnDestroy {
  protected destroyRef = inject(DestroyRef);
  props = input<InputProps<T>>();
  value = input<any>();
  multi = input(false);
  optionsEndpoint = input(null);
  hideTitle = input(false);
  disabled = input(false);
  emitKeyField = input(false);
  selection = output<FormGroupValue<any>>();
  mainValueControl: FormControl;
  optionsFilterControl: FormControl<string | null> = new FormControl<string | null>('');
  filteredOptions: ReplaySubject<InputPropOption[]> = new ReplaySubject<InputPropOption[]>(1);
  dataKey = 'key';
  displayClearButton = false;
  isAllSelected = signal(false);
  private readonly lookupRefService = inject(LookupRefService);
  public translationService = inject(L10nTranslationService);
  private propsInitialized = false;
  private optionsEndpointInitialized = false;
  private lastEndpoint: unknown = null;
  constructor() {
    effect(() => {
      const props = this.props();
      if (!this.propsInitialized) {
        this.propsInitialized = true;
        return;
      }
      if (props && props.options) {
        this.filteredOptions.next(props.options.slice());
      }
    });
    effect(() => {
      const endpoint = this.optionsEndpoint();
      if (!this.optionsEndpointInitialized) {
        this.optionsEndpointInitialized = true;
        return;
      }
      if (endpoint === this.lastEndpoint) {
        return;
      }
      this.lastEndpoint = endpoint;
      if (endpoint && this.mainValueControl) {
        this.filteredOptions.next([]);
        this.mainValueControl.setValue(untracked(() => this.value()));
        endpoint.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((options: any[]) => {
          if (options) {
            this.setDependantFieldOptions(options);
          }
        });
      }
    });
  }
  ngOnInit() {
    this.dataKey = this.props().dataKey || this.dataKey;
    this.displayClearButton = !!(Array.isArray(this.value()) && this.value().length);
    this.mainValueControl = new FormControl(this.value(), this.props().controlOptions || {});
    this.optionsFilterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filterValue: string) => this.filterOptions(filterValue));
    if (this.props().lookupCategory) {
      this.lookupRefService.loadDataFor(this.props().lookupCategory).subscribe((options: any[]) => {
        this.refineOptions(options);
      });
    } else if (this.optionsEndpoint()) {
      this.optionsEndpoint().subscribe((options: any[]) => {
        if (this.props().field === 'subtypes') {
          this.refineOptions(options);
        } else {
          this.setDependantFieldOptions(options);
        }
      });
    } else {
      if (this.props().options && this.props().options.length > 0 && this.props().options[0].key === this.props().allKey) {
        this.props().options.shift();
      }
      if (Array.isArray(this.value()) && this.value().length > 0) {
        if (
          this.value()[0] === this.props().allKey ||
          (this.value().length === this.props().options.length && this.props().allKey)
        ) {
          this.isAllSelected.set(true);
        }
      }
      this.filteredOptions.next(this.props().options && this.props().options.slice());
    }
    if (this.isAllSelected()) {
      this.toggleAll();
    }
    /**
     * Load cascade changes on init if props has targetField, lookupCategory and value
     * This is to handle the case when the parent filter has a default value and we need to load the child filter options based on that value
     * Example: Retail Sector -> Retail Subsector (Retail Sector has a default value, so we need to load the Retail Subsector options based on that value)
     * Note: This will not emit the selection event as the parent filter will emit the event and the child filter will be updated accordingly
     */
    if (
      this.props()[InputPropsEnum.targetField] &&
      this.props().controlOptions &&
      this.props().controlOptions.lookupCategory &&
      this.props().value
    ) {
      this.emitSelection({
        [this.props().field]: this.props().value,
        isAll: this.isAllSelected(),
        [InputPropsEnum.targetField]: this.props().targetField,
      });
    }
  }
  ngOnDestroy() {
    /* REQUIRED for untilDestroyed */
  }
  onSelectionChanged(event: MatSelectChange): void {
    if (this.props().type === 'multi-select' && this.props().allKey) {
      const isAllSelected =
        event.value[0] === this.props().allKey ||
        (event.value.length === this.props().options.length && !this.isAllSelected());
      if (isAllSelected !== this.isAllSelected()) {
        this.isAllSelected.set(isAllSelected);
        this.toggleAll();
      } else if (isAllSelected) {
        (event.value as any[]).shift();
        this.isAllSelected.set(false);
        this.mainValueControl.setValue(event.value);
      }
    }
    if (this.props().type !== 'multi-select') {
      const emitKey = this.emitKeyField() ? this.props().key : this.props().field;
      this.emitSelection({ [emitKey]: event.value });
    }
    this.displayClearButton = !!(Array.isArray(event.value) && event.value.length);
  }
  openedChange(event: boolean): void {
    if (!event) {
      let value = this.mainValueControl.value;
      if (this.isAllSelected() || (!!value && value[0] === this.props().allKey)) {
        /**
         * * 'All' option string is only for UI purposes and should not be used in the filtering request...
         * * 'All' option equals to not filters at all...
         */
        value = [];
      }
      const emitKey = this.emitKeyField() ? this.props().key : this.props().field;
      this.emitSelection({ [emitKey]: value, isAll: this.isAllSelected() });
    }
  }
  clearFilter() {
    this.optionsFilterControl.setValue('');
    if (this.multi() && this.mainValueControl) {
      this.mainValueControl.setValue([], { emitEvent: true });
    }
    this.displayClearButton = false;
  }
  onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space' || event.key === ' ') {
      event.stopPropagation();
    }
  }
  /**
   * Filters out the select options depending on a string input and assigns them to
   * the {@see filteredOptions} ReplaySubject.
   */
  private filterOptions(filterValue: string): void {
    if (!this.props().options) {
      return;
    }
    let search = filterValue;
    if (!search) {
      this.filteredOptions.next(this.props().options.slice());
    } else {
      search = search.toLowerCase();
      this.filteredOptions.next(
        this.props().options.filter(option => (option.value || option.key).toLowerCase().indexOf(search) > -1)
      );
    }
  }
  private setDependantFieldOptions(options: any[]) {
    if (options && options.length) {
      // geo api returns with a default option as All. Here we are making it as default selection.
      options[0].name = options[0].value = this.translationService.translate('recordsClipboard.select');
      options[0].key = null;
      const finalOptions = [...options];
      if (this.props().addNoneOption != null) {
        finalOptions.unshift(this.props().addNoneOption);
      }
      this.filteredOptions.next(finalOptions);
    }
  }
  private toggleAll(): void {
    if (this.isAllSelected()) {
      this.mainValueControl.setValue([this.props().allKey, ...this.props().options.map(opt => opt.key)]);
    } else {
      this.mainValueControl.setValue([]);
    }
  }
  private emitSelection(value: FormGroupValue<any>): void {
    this.selection.emit(value);
  }
  private refineOptions(options) {
    const finalOptions = [...options];
    if (this.props().addNoneOption != null) {
      finalOptions.unshift(this.props().addNoneOption);
    }
    if (finalOptions.length > 0 && finalOptions[0].key === this.props().allKey) {
      finalOptions.shift();
    }
    if (Array.isArray(this.value()) && Array.isArray(this.props().options) && this.value().length > 0) {
      if (
        this.value()[0] === this.props().allKey ||
        (this.value().length === this.props().options.length && this.props().allKey)
      ) {
        this.isAllSelected.set(true);
      }
    }
    this.filteredOptions.next(finalOptions);
  }
}
